<template>
  <div class="wm_side_panel" :style="{ width: width + 'px' }">
    <div
      class="wm_side_panel_resize"
      @pointerdown="start_drag"
      @pointermove="on_drag"
      @pointerup="stop_drag"
      @pointercancel="stop_drag"
    ></div>

    <div class="wm_side_panel_body">
      <div class="wm_side_panel_bar">
        <button class="wm_side_panel_close" :title="'Close'" @click="$emit('close')">&times;</button>
        <slot name="header" />
      </div>
      <div class="wm_side_panel_content" :class="{ padded: padded }">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SidePanel",

  emits: ["close", "width", "dragging"],

  props: {
    initial_width: { type: Number, default: 800 },
    min_width: { type: Number, default: 450 },
    max_width: { type: Number, default: 0 },
    storage_key: { type: String, default: "" },
    padded: { type: Boolean, default: false },
  },

  data() {
    return {
      width: this.initial_width,
      dragging: false,
      drag_start_x: 0,
      drag_start_width: 0,
      pointer_id: null,
    };
  },

  mounted() {
    this.set_width(this.stored_width() || this.initial_width);
    window.addEventListener("resize", this.on_window_resize);
  },

  unmounted() {
    window.removeEventListener("resize", this.on_window_resize);
    this.dragging = false;
    document.body.style.userSelect = "";
  },

  watch: {
    dragging(active) {
      this.$emit("dragging", active);
    },
  },

  methods: {
    limit() {
      return this.max_width > 0 ? this.max_width : window.innerWidth - 80;
    },

    clamp(width) {
      return Math.max(this.min_width, Math.min(width, this.limit()));
    },

    set_width(width) {
      this.width = this.clamp(width);
      this.$emit("width", this.width);
    },

    stored_width() {
      if (!this.storage_key)
        return 0;
      try {
        return Number(localStorage.getItem(`side_panel_width_${this.storage_key}`)) || 0;
      } catch (e) {
        return 0;
      }
    },

    remember_width() {
      if (!this.storage_key)
        return;
      try {
        localStorage.setItem(`side_panel_width_${this.storage_key}`, String(this.width));
      } catch (e) {
        console.warn("Could not remember the panel width.", e);
      }
    },

    start_drag(event) {
      event.preventDefault();
      this.dragging = true;
      this.drag_start_x = event.clientX;
      this.drag_start_width = this.width;
      this.pointer_id = event.pointerId;
      document.body.style.userSelect = "none";

      event.target.setPointerCapture(event.pointerId);
    },

    on_drag(event) {
      if (!this.dragging)
        return;
      this.set_width(this.drag_start_width - (event.clientX - this.drag_start_x));
    },

    stop_drag(event) {
      if (!this.dragging)
        return;
      this.dragging = false;
      document.body.style.userSelect = "";

      if (event != null && event.target.hasPointerCapture(this.pointer_id))
        event.target.releasePointerCapture(this.pointer_id);

      this.remember_width();
    },

    on_window_resize() {
      this.set_width(this.width);
    },
  },
};
</script>

<style scoped>
.wm_side_panel {
  position: fixed;
  top: 47px;
  right: 0;
  bottom: 0;
  background-color: #fff;
  border-left: 1px solid #ccc;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  display: flex;

  --wm_sticky_offset: 0px;
}

.wm_side_panel_resize {
  flex: 0 0 6px;
  cursor: col-resize;
  touch-action: none;
  background-color: #f2f2f2;
  border-right: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
}

.wm_side_panel_resize:hover {
  background-color: #d0d0d0;
}

.wm_side_panel_body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.wm_side_panel_bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 16px 6px 8px;
  border-bottom: 1px solid #e0e0e0;
}

.wm_side_panel_close {
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  color: #666;
  cursor: pointer;
  padding: 0 6px;
}

.wm_side_panel_close:hover {
  color: #000;
}

.wm_side_panel_content {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.wm_side_panel_content.padded {
  padding: 10px;
  --wm_sticky_pad: 10px;
}
</style>
